import { useEffect, useState } from 'react';
import { Modal, Spinner } from 'reactstrap';
import Button from '../ui/Button';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { callApi, IApiErrorResponse } from '@/api/callApi';
import {
  IAddEmployeeForm,
  ISalaryPageStates,
} from '@/interfaces/employee.model';
import { useFormContext } from 'react-hook-form';
import {
  ISalaryHeadConfig,
  ISalaryHeadDetailResponse,
} from '@/interfaces/salary.model';
import PrimaryInput from '../ui/PrimaryInput';
import ErrorMessage from '../common/ErrorMessage';
import { showToastNotification } from '@/utility/helper';
import { IDeleteResponse } from '@/interfaces/common.model';
interface IEditSalaryModalProps {
  isOpen: boolean;
  toggle: () => void;
  empId: string | null;
  setPageStates: React.Dispatch<React.SetStateAction<ISalaryPageStates>>;
  getEmployeeDetail: () => Promise<void>;
}
const EditSalaryModal = ({
  isOpen,
  toggle,
  empId,
  setPageStates,
  getEmployeeDetail,
}: IEditSalaryModalProps) => {
  const [salary, setSalary] = useState<ISalaryHeadConfig[]>([]);
  const [isLoading] = useState<boolean>(false);
  const [isLoadingUserSalary, setIsLoadingUserSalary] =
    useState<boolean>(false);
  const {
    setValue,
    register,
    getValues,
    formState: { errors },
  } = useFormContext<IAddEmployeeForm>();
  useEffect(() => {
    if (isOpen && empId) {
      getUserSalary();
    }
  }, [isOpen, empId]);

  const getUserSalary = async () => {
    setIsLoadingUserSalary(true);

    const res: ISalaryHeadDetailResponse = await callApi(
      API_URL + API_ENDPOINT.getUserSalary + `?userId=${empId}`,
      { method: 'GET' },
    );
    setIsLoadingUserSalary(false);
    if (res?.status === true) {
      const filtered = res.data.filter(
        (i) => i?.sitesalaryhead?.salaryhead?.takeInput === true,
      );

      // Set salary state
      setSalary(filtered);

      // Set default form values
      const defaultSalary = filtered.map((item) => ({
        amount: item.amount || 0,
        siteSalaryHeadId: item?.sitesalaryhead?.id,
      }));

      setValue('userSalary', defaultSalary);
    }
  };
  const handleUpdateSalary = async () => {
    try {
      const data = getValues();
      const userSalary = data?.userSalary;
      setPageStates((prev) => ({
        ...prev,
        isLoadingSalaryUpdation: true,
      }));

      const res: IDeleteResponse = await callApi(
        API_URL + API_ENDPOINT.updateUserSalary + `?userId=${empId}`,
        { method: 'POST', data: { userSalary } },
      );

      setPageStates((prev) => ({
        ...prev,
        isLoadingSalaryUpdation: false,
      }));

      if (res?.status === true) {
        showToastNotification('success', 'Employee Salary Updated');
        await getEmployeeDetail();
        toggle();
      }
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setPageStates((prev) => ({
        ...prev,
        isLoadingSalaryUpdation: false,
      }));
    }
  };

  return (
    <Modal
      className="modal-dialog modal-dialog-scrollable modal-xl modal-dialog-centered"
      isOpen={isOpen}
      toggle={toggle}
    >
      <div className="modal-content">
        <div className="modal-header border-0">
          <h1 className="modal-title fs-3 rajdhani-semibold">Update Salary</h1>
        </div>

        <div className="modal-body p-1">
          {isLoadingUserSalary ? (
            <div className="d-flex justify-content-center my-5">
              <Spinner />
            </div>
          ) : (
            <div className="px-4 py-4">
              <div className="row g-4">
                {salary?.length > 0 &&
                  salary.map((item, index) => {
                    const fieldName = `userSalary[${index}].amount`;
                    const error = errors.userSalary?.[index]?.amount;
                    const isEarning =
                      item?.sitesalaryhead?.salaryhead?.type === 'earning';

                    return (
                      <div
                        className="col-md-6"
                        key={item?.sitesalaryhead?.salaryhead?.id}
                      >
                        <div className="d-flex align-items-start border rounded-3 bg-white shadow-sm p-3 position-relative">
                          <div className="w-100">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <strong className="text-dark">
                                {item?.sitesalaryhead?.salaryhead?.name}
                              </strong>
                              <span
                                className={`badge text-capitalize bg-${
                                  isEarning ? 'success-subtle' : 'danger-subtle'
                                } text-${
                                  isEarning ? 'success' : 'danger'
                                } fw-semibold`}
                              >
                                {item?.salaryhead?.type}
                              </span>
                            </div>
                            <PrimaryInput
                              label="Amount"
                              id={fieldName}
                              type="number"
                              // onWheel={(e) => e.target.blur()}
                              className={`form-control ${
                                error ? 'is-invalid' : ''
                              }`}
                              defaultValue={item?.amount || ''}
                              min={0}
                              {...register(`userSalary.${index}.amount`, {
                                required: true,
                                min: 0,
                              })}
                              placeholder={`Enter amount`}
                            />
                            {errors?.userSalary && (
                              <ErrorMessage
                                message={`Please enter a valid amount for{" "}
                                      ${item?.sitesalaryhead?.salaryhead?.name}.`}
                              />
                            )}
                            <input
                              type="hidden"
                              {...register(
                                `userSalary.${index}.siteSalaryHeadId`,
                              )}
                              value={item?.id}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
          {/* <div className="d-flex justify-content-center align-items-start mt-5">
            <div
              className="card border-0"
              style={{ backgroundColor: '#f9fafd' }}
            >
              <div className="px-4 py-3 border-bottom d-flex align-items-center bg-white rounded-top-4">
                <i className="bi bi-wallet2 fs-4 me-2 text-primary"></i>
                <h4 className="mb-0 text-primary">Set Salary Details</h4>
              </div>
              {isLoadingUserSalary ? (
                <div className="d-flex justify-content-center my-5">
                  <Spinner />
                </div>
              ) : (
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="px-4 py-4">
                    <div className="row g-4">
                      {salary?.length > 0 &&
                        salary.map((item, index) => {
                          const fieldName = `userSalary[${index}].amount`;
                          const error =
                            errorsSalary?.userSalary?.[index]?.amount;
                          const isEarning =
                            item?.sitesalaryhead?.salaryhead?.type ===
                            'earning';

                          return (
                            <div
                              className="col-md-6"
                              key={item?.sitesalaryhead?.salaryhead?.id}
                            >
                              <div className="d-flex align-items-start border rounded-3 bg-white shadow-sm p-3 position-relative">
                                <div className="w-100">
                                  <div className="d-flex justify-content-between align-items-center mb-1">
                                    <strong className="text-dark">
                                      {item?.sitesalaryhead?.salaryhead?.name}
                                    </strong>
                                    <span
                                      className={`badge text-capitalize bg-${
                                        isEarning
                                          ? 'success-subtle'
                                          : 'danger-subtle'
                                      } text-${
                                        isEarning ? 'success' : 'danger'
                                      } fw-semibold`}
                                    >
                                      {item?.sitesalaryhead?.salaryhead?.type}
                                    </span>
                                  </div>

                                  <label
                                    htmlFor={fieldName}
                                    className="form-label fw-semibold small text-muted"
                                  >
                                    Amount
                                  </label>
                                  <input
                                    type="number"
                                    onWheel={(e) => e.target.blur()}
                                    className={`form-control ${
                                      error ? 'is-invalid' : ''
                                    }`}
                                    id={fieldName}
                                    defaultValue={item?.amount}
                                    min={0}
                                    {...registerSalary(
                                      `userSalary.${index}.amount`,
                                      {
                                        required: true,
                                        min: 0,
                                      },
                                    )}
                                    placeholder={`Enter amount`}
                                  />
                                  {error && (
                                    <div className="invalid-feedback">
                                      Please enter a valid amount for{' '}
                                      {item?.sitesalaryhead?.salaryhead?.name}.
                                    </div>
                                  )}

                                  <input
                                    type="hidden"
                                    {...registerSalary(
                                      `userSalary.${index}.siteSalaryHeadId`,
                                    )}
                                    value={item?.sitesalaryhead?.id}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div> */}
        </div>

        <div className="modal-footer p-4 border-0">
          <button type="button" className="btn btn-danger" onClick={toggle}>
            Close
          </button>

          <Button
            isLoading={isLoading}
            type="button"
            className="btn btn-primary px-5"
            onClick={handleUpdateSalary}
            disabled={isLoading}
          >
            Update
          </Button>
        </div>
      </div>
    </Modal>
  );
};
export default EditSalaryModal;
